// Background sync manager — makes the local-first outbox drain reliably so a
// queued sale ALWAYS reaches the server eventually, with zero further operator
// action (spec §8.4). Triggers a drain on: startup, the `online` event, tab
// re-focus, and a periodic safety-net timer. Exposes online/pending/syncing
// state for the header indicator so the operator always knows where they stand.

import { conflictCount, pendingCount } from "./outbox";
import { postSync } from "./poster";
import { drainOutbox } from "./sync";

export interface SyncState {
  online: boolean;
  pending: number;
  conflicts: number; // server hard-rejections needing manual resolution
  syncing: boolean;
  lastError: string | null;
}

let state: SyncState = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  pending: 0,
  conflicts: 0,
  syncing: false,
  lastError: null,
};

const listeners = new Set<() => void>();

function set(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) {
    l();
  }
}

export function subscribeSync(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSyncState(): SyncState {
  return state;
}

export async function refreshPending(): Promise<void> {
  try {
    const [pending, conflicts] = await Promise.all([pendingCount(), conflictCount()]);
    set({ pending, conflicts });
  } catch {
    // an IndexedDB read hiccup must never crash the UI
  }
}

/** Drain the outbox now. Re-entrant-safe (a single in-flight drain at a time);
 *  server is idempotent by tx_uuid so overlapping triggers are harmless. */
export async function drainNow(): Promise<void> {
  if (state.syncing) {
    return;
  }
  set({ syncing: true });
  try {
    await drainOutbox(postSync);
    set({ lastError: null });
  } catch (err) {
    // Offline / token expired — items stay pending and retry on the next trigger.
    set({ lastError: err instanceof Error ? err.message : "sync failed" });
  } finally {
    set({ syncing: false });
    await refreshPending();
  }
}

let started = false;
let timer: ReturnType<typeof setInterval> | null = null;

export function startSyncManager(intervalMs = 20_000): void {
  if (started || typeof window === "undefined") {
    return;
  }
  started = true;

  window.addEventListener("online", () => {
    set({ online: true });
    void drainNow();
  });
  window.addEventListener("offline", () => {
    set({ online: false });
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void drainNow();
    }
  });
  // Safety net: queued sales sync even if the operator takes no further action.
  timer = setInterval(() => {
    if (navigator.onLine) {
      void drainNow();
    }
  }, intervalMs);

  void drainNow();
}

/** Test helper: reset the singleton so each test starts clean. */
export function _resetSyncManagerForTests(): void {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  started = false;
  state = { online: true, pending: 0, conflicts: 0, syncing: false, lastError: null };
  listeners.clear();
}
